import React, { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { joiResolver } from "@hookform/resolvers/joi";
import * as Joi from "joi";
import {
  deadlineCardFormProps,
  INormalizedLmcDataType,
  lmcOptionComponentProps,
} from "./types";
import LmcUtil from "./util";
import { Select } from "flowbite-react";
import { HiOutlineTrash } from "react-icons/hi";
import LMCButtons from "../../../public/LMCButtons";
import { currencyMapper } from "./AmountCard";
import {
  checkKeydownDecimalValue,
  onInputCheckDecimal,
  onPasteCheckDecimal,
} from "../../../../utils/inputs";
import { CustomInput } from "../../../atoms/CustomInput";
import { CustomButton } from "../../../atoms/CustomButton";
import { FieldLabel } from "../../../atoms/FieldLabel";

const uniqueValues = (arr: Array<any>, key: string) => {
  const seen = new Set();
  for (const obj of arr) {
    const value = obj[key];
    if (value !== null && value !== "" && seen.has(value)) {
      return false;
    }
    seen.add(value);
  }
  return true;
};

const validatePrecision = (value: number, precision: number) => {
  if (typeof value !== "number") {
    throw new Error("Input value must be a number");
  }

  const stringValue = value.toString();
  const decimalIndex = stringValue.indexOf(".");

  if (decimalIndex === -1) {
    return true;
  }

  const decimalPlaces = stringValue.length - decimalIndex - 1;
  return decimalPlaces <= precision;
};

const atLeastOneItemIsFilled = (arr: Array<{ value: number }>) => {
  let hasOne = arr.find(
    (item) =>
      Object.keys(item).length > 0 && item.value > 0 && item.value !== null
  );

  return hasOne ? true : false;
};

const createSchema = (t: any) =>
  Joi.object()
    .keys({
      moneyType: Joi.string()
        .valid("amount", "percentage")
        .required()
        .label(t("dashboard.features.lmc.deadlineCard.validation.type"))
        .messages({
          "any.only": t(
            "dashboard.features.lmc.deadlineCard.validation.typeRequired"
          ),
        }),
      moneyValues: Joi.array()
        .items(
          Joi.object({
            value: Joi.number()
              .custom((value, helper) => {
                if (!validatePrecision(value, 5)) {
                  return helper.message(
                    t(
                      "dashboard.features.lmc.deadlineCard.validation.maxDecimal5"
                    ) as any
                  );
                }
                return value;
              })
              .positive()
              .max(9999999999)
              .allow(null)
              .allow("")
              .optional()
              .label(
                t(
                  "dashboard.features.lmc.deadlineCard.validation.defaultAmount"
                )
              )
              .when("moneyType", {
                is: "percentage",
                then: Joi.number().min(1),
                otherwise: Joi.number().min(0.00001),
              }),
          })
        )
        .min(1)
        .max(3)
        .required()
        .custom((value, helper) => {
          if (!uniqueValues(value, "value")) {
            return helper.message(
              t(
                "dashboard.features.lmc.deadlineCard.validation.uniqueAmounts"
              ) as any
            );
          }
          return value;
        })
        .custom((value, helper) => {
          if (!atLeastOneItemIsFilled(value)) {
            return helper.message(
              t(
                "dashboard.features.lmc.deadlineCard.validation.atLeastOneAmount"
              ) as any
            );
          }
          return value;
        })
        .label(
          t("dashboard.features.lmc.deadlineCard.validation.defaultAmounts")
        )
        .messages({
          "array.includesRequiredKnowns": t(
            "dashboard.features.lmc.deadlineCard.validation.addOneValue"
          ),
        }),
      moneyDefaultValue: Joi.string()
        .optional()
        .label(
          t("dashboard.features.lmc.deadlineCard.validation.defaultValue")
        ),
      moneyCurrency: Joi.alternatives().conditional("moneyType", {
        is: "amount",
        then: Joi.string()
          .required()
          .label(
            t("dashboard.features.lmc.deadlineCard.validation.defaultValue")
          ),
        otherwise: Joi.optional(),
      }),
      monyCustomValue: Joi.boolean()
        .required()
        .label(t("dashboard.features.lmc.deadlineCard.validation.customValue")),
      timeValues: Joi.array()
        .items(
          Joi.object({
            value: Joi.number()
              .custom((value, helper) => {
                if (!validatePrecision(value, 2)) {
                  return helper.message(
                    t(
                      "dashboard.features.lmc.deadlineCard.validation.maxDecimal2"
                    ) as any
                  );
                }
                return value;
              })
              .positive()
              .min(1)
              .max(9999)
              .allow(null)
              .allow("")
              .optional()
              .label(
                t(
                  "dashboard.features.lmc.deadlineCard.validation.defaultAmount"
                )
              ),
          })
        )
        .min(1)
        .max(3)
        .required()
        .custom((value, helper) => {
          if (!uniqueValues(value, "value")) {
            return helper.message(
              t(
                "dashboard.features.lmc.deadlineCard.validation.uniqueDates"
              ) as any
            );
          }
          return value;
        })
        .custom((value, helper) => {
          if (!atLeastOneItemIsFilled(value)) {
            return helper.message(
              t(
                "dashboard.features.lmc.deadlineCard.validation.atLeastOneDate"
              ) as any
            );
          }
          return value;
        })
        .label(t("dashboard.features.lmc.deadlineCard.validation.defaultDates"))
        .messages({
          "array.includesRequiredKnowns": t(
            "dashboard.features.lmc.deadlineCard.validation.addOneValue"
          ),
        }),
      timeDefaultValue: Joi.string()
        .optional()
        .label(
          t("dashboard.features.lmc.deadlineCard.validation.defaultValue")
        ),
      timeCustomValue: Joi.boolean()
        .required()
        .label(t("dashboard.features.lmc.deadlineCard.validation.customValue")),
      comment: Joi.string()
        .max(100)
        .optional()
        .allow("", null)
        .label(t("dashboard.features.lmc.deadlineCard.validation.comment"))
        .messages({
          "string.max": t(
            "dashboard.features.lmc.incrementalCard.validation.commentMax"
          ),
        }),
    })
    .required();

const DeadlineCard = ({
  label,
  optionData,
  onSubmitCardData,
  playgroundDataHandler,
  handleCancelEdit,
  tutorial,
}: lmcOptionComponentProps) => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    control,
    trigger,
  } = useForm<deadlineCardFormProps>({
    resolver: joiResolver(createSchema(t)),
  });

  const moneyFieldArray = useFieldArray({
    control,
    name: "moneyValues",
  });

  const timeFieldArray = useFieldArray({
    control,
    name: "timeValues",
  });

  useEffect(() => {
    if (optionData) {
      let denormalizedData = LmcUtil.unwrapper(
        "DEADLINE_CARD",
        optionData
      ) as deadlineCardFormProps;

      reset({ ...denormalizedData });
    }
  }, [optionData, reset]);

  const onSubmit = (data: deadlineCardFormProps) => {
    const newData = {
      ...data,
      moneyValues: data.moneyValues
        .filter((item: any) => item.value !== null)
        .filter((item: any) => item.value !== "")
        .map((v) => v.value),
      timeValues: data.timeValues
        .filter((item: any) => item.value !== null)
        .filter((item: any) => item.value !== "")
        .map((v) => v.value),
    };

    const normalizedData = LmcUtil.wrapper(
      "DEADLINE_CARD",
      newData
    ) as INormalizedLmcDataType;

    onSubmitCardData("DEADLINE_CARD", normalizedData).then(() => {
      reset();
    });
  };

  useEffect(() => {
    watch((value, { name, type }) => {
      const newData = {
        ...value,
        moneyValues: value.moneyValues
          ?.filter((item: any) => item.value !== null)
          ?.filter((item: any) => item.value !== "")
          ?.map((v) => v?.value),
        timeValues: value.timeValues
          ?.filter((item: any) => item.value !== null)
          ?.filter((item: any) => item.value !== "")
          ?.map((v) => v?.value),
      };

      playgroundDataHandler({
        type: "DEADLINE_CARD",
        data: LmcUtil.wrapper(
          "DEADLINE_CARD",
          newData
        ) as INormalizedLmcDataType,
      });
    });
  }, [playgroundDataHandler, watch]);

  return (
    <div className="lmc-card point-card w-full">
      <form onSubmit={handleSubmit(onSubmit)} className="w-full space-y-6">
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <div className="mb-5">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-3 mb-2">
              <span className="flex items-center justify-center w-7 h-7 bg-blue-500 text-white rounded-lg text-sm font-semibold shadow-sm">
                1
              </span>
              {t("dashboard.features.lmc.deadlineCard.section1Title")}
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              {t("dashboard.features.lmc.deadlineCard.section1Desc")}
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <FieldLabel
                htmlFor="moneyType"
                className="block mb-2 text-sm font-medium text-gray-700"
              >
                {t("dashboard.features.lmc.deadlineCard.label1")}
              </FieldLabel>
              <Select
                translate="no"
                color={!!errors.moneyType ? "failure" : "gray"}
                aria-label="Default select example"
                helperText={errors.moneyType?.message}
                {...register("moneyType", {
                  onChange(event) {
                    reset({
                      moneyType: event.target.value,
                      comment: "",
                      moneyCurrency: "",
                      moneyValues: [],
                      monyCustomValue: false,
                      timeCustomValue: false,
                      timeValues: [],
                    });
                  },
                })}
              >
                <option value="">
                  {t("dashboard.features.lmc.deadlineCard.placeholder1")}
                </option>
                <option value="amount">
                  {t("dashboard.features.lmc.deadlineCard.amount")}
                </option>
                <option value="percentage">
                  {t("dashboard.features.lmc.deadlineCard.percentage")}
                </option>
              </Select>
            </div>

            {watch("moneyType") && watch("moneyType") === "amount" && (
              <div>
                <FieldLabel
                  htmlFor="moneyCurrency"
                  className="block mb-2 text-sm font-medium text-gray-700"
                >
                  {t("dashboard.features.lmc.deadlineCard.label4")}
                </FieldLabel>
                <Select
                  translate="no"
                  color={!!errors.moneyCurrency ? "failure" : "gray"}
                  aria-label="Default select example"
                  helperText={errors.moneyCurrency?.message}
                  {...register("moneyCurrency")}
                >
                  <option value="">
                    {t("dashboard.features.lmc.deadlineCard.placeholder6")}
                  </option>
                  {Object.entries(currencyMapper).map(
                    ([code, { symbol, name }]) => (
                      <option key={code} value={code}>
                        {`${code} - ${name} (${symbol})`}
                      </option>
                    )
                  )}
                </Select>
              </div>
            )}

            <div className="space-y-3">
              {moneyFieldArray.fields.map((item, index) => (
                <div key={item.id}>
                  <CustomInput
                    name={`moneyValues.${index}.value`}
                    control={control}
                    label={
                      t("dashboard.features.lmc.deadlineCard.key13") as string
                    }
                    className={`${
                      moneyFieldArray.fields.length > 1
                        ? "col-span-6"
                        : "col-span-7"
                    }`}
                    helperText={
                      (errors.moneyValues?.[index]?.value?.message ||
                      (watch("moneyType") === "percentage" &&
                        (watch("moneyValues")?.[index]?.value as number) > 100)
                        ? t(
                            "dashboard.features.lmc.deadlineCard.errorMaxPercentage"
                          )
                        : "") as string
                    }
                    error={
                      errors.moneyValues?.[index]?.value ||
                      (watch("moneyType") === "percentage" &&
                        (watch("moneyValues")?.[index]?.value as number) > 100)
                    }
                    type={watch("moneyType") === "amount" ? "text" : "number"}
                    step=".01"
                    inputMode={
                      watch("moneyType") === "amount" ? "decimal" : "numeric"
                    }
                    onInput={(e) =>
                      watch("moneyType") === "amount" && onInputCheckDecimal(e)
                    }
                    onKeyDown={(e) =>
                      watch("moneyType") === "amount" &&
                      checkKeydownDecimalValue(e)
                    }
                    onPaste={(e) =>
                      watch("moneyType") === "amount" && onPasteCheckDecimal(e)
                    }
                    max={watch("moneyType") === "percentage" ? 100 : 9999999999}
                    placeholder={
                      t(
                        "dashboard.features.lmc.deadlineCard.placeholder2"
                      ) as string
                    }
                    onValueChange={() => {
                      trigger("moneyValues");
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    rightComponent={
                      <div className="flex items-center gap-2">
                        <div className="text-gray-500 text-sm font-medium">
                          {watch("moneyType") === "percentage"
                            ? "%"
                            : watch("moneyType") === "amount" &&
                              watch("moneyCurrency")
                            ? currencyMapper[watch("moneyCurrency") as string]
                                ?.symbol || watch("moneyCurrency")
                            : ""}
                        </div>
                        {moneyFieldArray.fields.length > 1 && (
                          <div className="col-span-1 flex self-stretch items-center">
                            <CustomButton
                              variant="none"
                              size="icon"
                              className="rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                              onClick={() => moneyFieldArray.remove(index)}
                              icon={<HiOutlineTrash size={18} />}
                            />
                          </div>
                        )}
                      </div>
                    }
                  />
                </div>
              ))}

              {watch("moneyValues")?.length < 3 && (
                <CustomButton
                  onClick={() => moneyFieldArray.append({ value: null })}
                  className="w-full bg-white border-2 border-dashed border-blue-300 text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-all"
                  title={
                    watch("moneyValues")?.length === 0
                      ? t("dashboard.features.lmc.deadlineCard.key1First")
                      : t("dashboard.features.lmc.deadlineCard.key1")
                  }
                />
              )}

              {!!errors.moneyValues && (
                <p className="text-red-600 text-sm font-medium bg-red-50 border border-red-200 rounded-lg p-3">
                  {errors.moneyValues?.message?.toString()}
                </p>
              )}
            </div>
          </div>

          <div className="mt-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <label className="switch shrink-0 mt-0.5">
                <input type="checkbox" {...register("monyCustomValue")} />
                <div className="slider"></div>
              </label>
              <div className="flex-1">
                <span className="text-gray-900 text-sm font-semibold block mb-1">
                  {t("dashboard.features.lmc.deadlineCard.customValueTitle")}
                </span>
                <span className="text-gray-700 text-xs leading-relaxed">
                  {t("dashboard.features.lmc.deadlineCard.label5")}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <div className="mb-5">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-3 mb-2">
              <span className="flex items-center justify-center w-7 h-7 bg-blue-500 text-white rounded-lg text-sm font-semibold shadow-sm">
                2
              </span>
              {t("dashboard.features.lmc.deadlineCard.section2Title")}
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              {t("dashboard.features.lmc.deadlineCard.section2Desc")}
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-3">
              {timeFieldArray.fields.map((item, index) => (
                <div key={item.id}>
                  <CustomInput
                    control={control}
                    name={`timeValues.${index}.value`}
                    label={
                      t("dashboard.features.lmc.deadlineCard.key14") as string
                    }
                    className={`${
                      timeFieldArray.fields.length > 1
                        ? "col-span-10"
                        : "col-span-12"
                    }`}
                    error={errors.timeValues?.[index]?.value}
                    type="number"
                    min={1}
                    placeholder={
                      t(
                        "dashboard.features.lmc.deadlineCard.placeholder2Duration"
                      ) as string
                    }
                    onValueChange={() => {
                      trigger("timeValues");
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    rightComponent={
                      <div className="grid grid-cols-2 items-center gap-2">
                        <div className="text-gray-500 text-sm">
                          {t("dashboard.features.lmc.deadlineCard.key15")}
                        </div>
                        {timeFieldArray.fields.length > 1 && (
                          <CustomButton
                            variant="none"
                            size="icon"
                            className="rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                            onClick={() => timeFieldArray.remove(index)}
                            icon={<HiOutlineTrash size={18} />}
                          />
                        )}
                      </div>
                    }
                  />
                </div>
              ))}

              {watch("timeValues")?.length < 3 && (
                <CustomButton
                  onClick={() => timeFieldArray.append({ value: null })}
                  className="w-full bg-white border-2 border-dashed border-blue-300 text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-all"
                  title={
                    watch("timeValues")?.length === 0
                      ? t("dashboard.features.lmc.deadlineCard.key6First")
                      : t("dashboard.features.lmc.deadlineCard.key6")
                  }
                />
              )}

              {!!errors.timeValues && (
                <p className="text-red-600 text-sm font-medium bg-red-50 border border-red-200 rounded-lg p-3">
                  {errors.timeValues?.message?.toString()}
                </p>
              )}
            </div>
          </div>

          <div className="mt-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <label className="switch shrink-0 mt-0.5">
                <input type="checkbox" {...register("timeCustomValue")} />
                <div className="slider"></div>
              </label>
              <div className="flex-1">
                <span className="text-gray-900 text-sm font-semibold block mb-1">
                  {t(
                    "dashboard.features.lmc.deadlineCard.timeCustomValueTitle"
                  )}
                </span>
                <span className="text-gray-700 text-xs leading-relaxed">
                  {t("dashboard.features.lmc.deadlineCard.label7")}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <div className="mb-5">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-3 mb-2">
              <span className="flex items-center justify-center w-7 h-7 bg-blue-500 text-white rounded-lg text-sm font-semibold shadow-sm">
                3
              </span>
              {t("dashboard.features.lmc.deadlineCard.section3Title")}
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              {t("dashboard.features.lmc.deadlineCard.section3Desc")}
            </p>
          </div>

          <div className="space-y-4">
            <CustomInput
              rows={4}
              name="comment"
              control={control}
              error={errors.comment}
              helperText={errors.comment?.message}
              placeholder={
                t("dashboard.features.lmc.deadlineCard.placeholder4") as string
              }
              label={t("dashboard.features.lmc.amountCard.label4") as string}
            />
          </div>
        </div>

        <div className="pt-2">
          <LMCButtons tutorial={tutorial} handleCancelEdit={handleCancelEdit} />
        </div>
      </form>
    </div>
  );
};

export const DeadlineCardUi = ({
  data,
}: {
  data: INormalizedLmcDataType | null;
}) => {
  const { t } = useTranslation();

  const currency = data?.pre_defined_value?.currency as string;
  const mapper = currencyMapper[currency];
  return (
    <div className="self-stretch  flex-col justify-start items-center gap-6 flex">
      <div className="w-full  bg-gray-800 rounded-xl flex-col justify-start items-center gap-6 inline-flex">
        <div className="self-stretch px-4 pb-5 flex-col justify-center items-center gap-3 flex pt-4">
          <div className="self-stretch justify-center items-start gap-3 inline-flex">
            <div className="grow shrink basis-0 flex-col justify-center items-center gap-3 inline-flex">
              <div className="text-center text-gray-400 text-xs font-bold font-figtree leading-normal">
                {t("dashboard.features.lmc.deadlineCard.key10")}
              </div>

              {data?.options?.custom_value && (
                <div className="self-stretch h-11 flex-col justify-center items-center gap-4 flex">
                  <div className="self-stretch h-11 bg-gray-700 rounded-full flex-col justify-center items-center gap-2 flex">
                    <div className="self-stretch grow shrink basis-0 px-6 py-2.5 justify-center items-center gap-2 inline-flex">
                      <div className="text-center text-gray-200 text-xs font-semibold font-figtree leading-normal">
                        {t("dashboard.features.lmc.deadlineCard.key11")}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {data?.pre_defined_value?.values?.map((v, index) => {
                if (
                  v > 0 &&
                  (data?.pre_defined_value?.type === "percentage"
                    ? v <= 100
                    : v <= 9999999999)
                )
                  return (
                    <div
                      key={index}
                      className="self-stretch h-11 flex-col justify-center items-center gap-4 flex"
                    >
                      <div className="self-stretch h-11 bg-gray-700 rounded-full flex-col justify-center items-center gap-2 flex">
                        <div className="self-stretch grow shrink basis-0 px-6 py-2.5 justify-center items-center gap-2 inline-flex">
                          <div
                            className={`text-center text-gray-200 text-base font-semibold font-figtree leading-normal ${
                              currency &&
                              mapper.dir === "rtl" &&
                              "direction-rtl"
                            }`}
                          >
                            {data?.pre_defined_value?.type === "amount" && (
                              <>
                                {currency &&
                                  mapper?.position === "before" &&
                                  (mapper?.type === "currency"
                                    ? mapper?.symbol
                                    : currency)}{" "}
                                {data?.pre_defined_value?.type === "amount"
                                  ? v
                                  : new Intl.NumberFormat().format(
                                      Math.abs(+v)
                                    )}{" "}
                                {currency &&
                                  mapper?.position === "after" &&
                                  (mapper?.type === "currency"
                                    ? mapper?.symbol
                                    : currency)}
                              </>
                            )}
                            {data?.pre_defined_value?.type === "percentage" && (
                              <>{Math.abs(+v)}%</>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                else return null;
              })}
            </div>
            <div className="grow shrink basis-0 flex-col justify-center items-center gap-3 inline-flex">
              <div className="text-center text-gray-400 text-xs font-bold font-figtree leading-normal">
                {t("dashboard.features.lmc.deadlineCard.key12")}
              </div>
              {data?.options?.pre_defined_time?.custom_value && (
                <div className="self-stretch h-11 flex-col justify-center items-center gap-4 flex">
                  <div className="self-stretch h-11 bg-gray-700 rounded-full flex-col justify-center items-center gap-2 flex">
                    <div className="self-stretch grow shrink basis-0 px-6 py-2.5 justify-center items-center gap-2 inline-flex">
                      <div className="text-center text-gray-200 text-xs font-semibold font-figtree leading-normal">
                        {t("dashboard.features.lmc.deadlineCard.key11")}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {data?.options?.pre_defined_time?.values?.map(
                (timeValue: number, index: number) => {
                  if (timeValue > 0 && timeValue <= 9999)
                    return (
                      <div
                        key={index}
                        className="self-stretch h-11 flex-col justify-center items-center gap-4 flex"
                      >
                        <div className="self-stretch h-11 bg-gray-700 rounded-full flex-col justify-center items-center gap-2 flex">
                          <div className="self-stretch grow shrink basis-0 px-6 py-2.5 justify-center items-center gap-2 inline-flex">
                            <div className="text-center text-gray-200 text-base font-semibold font-figtree leading-normal">
                              {Math.abs(+timeValue)}{" "}
                              {t("dashboard.features.lmc.deadlineCard.days")}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  else return null;
                }
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeadlineCard;
